import { useState, useEffect, Suspense, lazy } from "react";
import { Cloud, CheckCircle } from "lucide-react";
import { conversationManager } from "./managers/ConversationManager";
import { stateManager } from "./managers/StateManager";
import AiCursor from "./components/AiCursor";
import Sidebar from "./components/Sidebar";
import AuthOverlay from "./components/AuthOverlay";
import ProfileMenu from "./components/ProfileMenu";
import { authManager } from "./managers/AuthManager";
import { subscriptionManager } from "./managers/SubscriptionManager";
import UpgradeModal from "./components/UpgradeModal";
import UsageBanner from "./components/UsageBanner";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";

const DocumentView = lazy(() => import("./DocView"));
const TemplateGallery = lazy(() => import("./components/TemplateGallery"));
const AiScorePage = lazy(() => import("./components/AiScorePage"));
const AIJobFinder = lazy(() => import("./components/AIJobFinder"));
const ProfilePage = lazy(() => import("./components/ProfilePage"));
const PricingPage = lazy(() => import("./components/PricingPage"));

function App() {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);
  const [state, setState] = useState("idle");
  const [uiState, setUIState] = useState("idle");
  const [document, setDocument] = useState(null);
  const [lastResponse, setLastResponse] = useState("");
  const [cursorState, setCursorState] = useState({ x: 0, y: 0, visible: false, text: "" });
  const [activeService, setActiveService] = useState(() => {
    const path = window.location.pathname;
    if (path.includes('/pricing')) return 'pricing';
    if (path.includes('/drafts')) return 'drafts';
    if (path.includes('/ai_score')) return 'ai_score';
    if (path.includes('/job_finder')) return 'job_finder';
    return 'resume_creator';
  });
  const [user, setUser] = useState(authManager.getCurrentUser());
  const [subscription, setSubscription] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('resume_builds');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const unsubAuth = authManager.subscribe((currentUser) => {
      setUser(currentUser);
      // Load subscription when user logs in
      if (currentUser?.email) {
        subscriptionManager.loadSubscription(currentUser.email);
      } else {
        subscriptionManager.clear();
      }
    });

    const unsubSub = subscriptionManager.subscribe((sub) => {
      setSubscription(sub);
    });

    // Check for payment success/cancel URL params
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      // Reload subscription after successful payment
      const currentUser = authManager.getCurrentUser();
      if (currentUser?.email) {
        setTimeout(() => subscriptionManager.loadSubscription(currentUser.email), 1500);
      }
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('payment') === 'cancelled') {
      window.history.replaceState({}, '', window.location.pathname);
    }

    conversationManager.startSession();

    const unsubscribe = conversationManager.subscribe((newState) => {
      setState(newState);
      setIsRecording(newState === "listening");

      if (newState === "listening") {
        setError(null);
      }
    });

    const unsubDoc = conversationManager.subscribeDocument(setDocument);

    const unsubUI = stateManager.subscribe(setUIState);

    const unsubResponse = conversationManager.subscribeResponse(setLastResponse);

    const unsubCursor = conversationManager.subscribeCursor(setCursorState);

    const unsubSaving = conversationManager.subscribeSaving(setIsSaving);

    return () => {
      unsubAuth();
      unsubSub();
      unsubscribe();
      unsubDoc();
      unsubUI();
      unsubResponse();
      unsubCursor();
      unsubSaving();
    };
  }, []);

  const handleToggleRecording = () => {
    if (isRecording) {
      conversationManager.stopListening();
    } else {
      conversationManager.startListening();
    }
  };

  const handleTemplateSelect = async (template) => {
    // Check usage limit
    if (!subscriptionManager.canUse('resume_builds')) {
      setUpgradeFeature('resume_builds');
      setShowUpgradeModal(true);
      return;
    }

    // Record usage
    if (user?.email) {
      const usageResult = await subscriptionManager.recordUsage(user.email, 'resume_builds');
      if (!usageResult.success) {
        if (usageResult.limitReached) {
          setUpgradeFeature('resume_builds');
          setShowUpgradeModal(true);
        } else {
          setError(usageResult.error || "Failed to start resume builder");
        }
        return;
      }
    }

    conversationManager.initializeDocument(template);
    stateManager.showDocumentView();
  };

  const handleLoadResume = (resumeData) => {
    conversationManager.initializeDocument(resumeData);
    setActiveService('resume_creator');
    stateManager.showDocumentView();
  };



  const showRightPanel = uiState === "template_selection" || uiState === "document_focused" || uiState === "editing";

  // Check if PDF export is allowed
  const canExportPdf = subscription?.tier === 'pro' || subscription?.tier === 'max';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#0f172a' }}>
      {!user && <AuthOverlay />}
      <Sidebar activeService={activeService} setActiveService={setActiveService} />
      
      <div className="app-container" style={{ flex: 1, paddingLeft: '40px', position: 'relative' }}>
        <header className="header" style={activeService === 'ai_score' || activeService === 'job_finder' || activeService === 'drafts' || activeService === 'pricing' ? { display: 'none' } : {}}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <h1>Talk2Doc</h1>
            {user && (
              <div className={`saving-indicator ${isSaving ? 'saving' : 'saved'}`} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                fontSize: '0.75rem', 
                color: isSaving ? '#94a3b8' : '#10b981',
                padding: '4px 10px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '20px',
                transition: 'all 0.3s ease',
                opacity: (isSaving || (document && !isSaving)) ? 1 : 0,
                transform: (isSaving || (document && !isSaving)) ? 'translateY(0)' : 'translateY(-5px)',
                pointerEvents: (isSaving || (document && !isSaving)) ? 'auto' : 'none'
              }}>
                {isSaving ? <Cloud size={14} className="spin-slow" /> : <CheckCircle size={14} />}
                <span>{isSaving ? 'Saving...' : 'Saved'}</span>
              </div>
            )}
          </div>
        </header>

        <style>{`
          @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin-slow {
            animation: spin-slow 3s linear infinite;
          }
          .saving-indicator {
            backdrop-filter: blur(4px);
            border: 1px solid rgba(255,255,255,0.05);
          }
          .saving-indicator.saved {
            background: rgba(16, 185, 129, 0.08) !important;
            border-color: rgba(16, 185, 129, 0.2);
          }
        `}</style>

        {user && (
          <div className="no-print" style={{ position: 'absolute', top: '20px', right: '40px', zIndex: 50 }}>
            <ProfileMenu user={user} setActiveService={setActiveService} subscription={subscription} />
          </div>
        )}

        {activeService === 'ai_score' ? (
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#94a3b8' }}>Loading AI Score...</div>}>
            <AiScorePage />
          </Suspense>
        ) : activeService === 'job_finder' ? (
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#94a3b8' }}>Loading Job Finder...</div>}>
            <AIJobFinder />
          </Suspense>
        ) : activeService === 'drafts' ? (
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#94a3b8' }}>Loading Drafts...</div>}>
            <ProfilePage onLoadResume={handleLoadResume} />
          </Suspense>
        ) : activeService === 'pricing' ? (
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#94a3b8' }}>Loading Pricing...</div>}>
            <PricingPage />
          </Suspense>
        ) : (

      <div className="split-layout">
        <div className={`left-panel ${showRightPanel ? 'compressed' : ''}`}>

          <main className="main-content">
            {subscription && (
              <UsageBanner 
                used={subscription?.usage?.resume_builds || 0} 
                limit={subscription?.limits?.resume_builds || 1} 
                label="Resume Builds" 
                onUpgrade={() => setShowUpgradeModal(true)} 
              />
            )}
            <div className="orb-container">
              <div className={`orb ${state} ${isRecording ? 'listening' : ''}`}></div>
            </div>

            <div className="status-label">
              {!isRecording && state === "idle" && "Tap to Speak"}
            </div>

            <div className={`interaction-layer ${state}`}>



              <div className="status-indicator">
                <div className={`status-dot ${state}`}></div>
                <span className="status-text">
                  {state === "idle" && "Ready"}
                  {state === "listening" && "Listening..."}
                  {state === "thinking" && "Processing..."}
                  {state === "speaking" && "Speaking..."}
                </span>
              </div>
            </div>

            {error && <div className="error-msg">{error}</div>}

            <div className="response-area">
              {(isRecording || state === "thinking" || state === "speaking") && (
                <div className="ai-messages">
                  {isRecording ? "Listening..." :
                    state === "thinking" ? "Processing..." :
                      state === "speaking" ? "Speaking..." : ""}
                </div>
              )}
              {lastResponse && state === "idle" && (
                <div className="ai-messages" style={{ opacity: 0.8, fontSize: '0.9rem' }}>
                  <strong>AI:</strong> {lastResponse}
                </div>
              )}
            </div>
          </main>

          <footer className="controls-bottom" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            <button
              className={`mic-button ${isRecording ? 'recording' : ''}`}
              onClick={handleToggleRecording}
              aria-label={isRecording ? "Tap to stop" : "Tap to speak"}
            >
              {isRecording ? "STOP" : "TALK"}
            </button>
            <div className="no-print" style={{ position: 'relative' }}>
              {document && (
                <button
                  className="btn-secondary"
                  onClick={() => canExportPdf ? window.print() : setActiveService('pricing')}
                  style={{
                    minWidth: '120px',
                    opacity: canExportPdf ? 1 : 0.6,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    justifyContent: 'center'
                  }}
                >
                  {!canExportPdf && <span style={{ fontSize: '0.7rem', background: 'linear-gradient(135deg, #7c3aed, #a78bfa)', padding: '1px 6px', borderRadius: '4px', color: 'white', fontWeight: 700 }}>PRO</span>}
                  Export PDF
                </button>
              )}
            </div>
          </footer>
        </div>

        <div className={`right-panel ${showRightPanel ? 'visible' : ''}`}>
          {uiState === "template_selection" && (
            <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#94a3b8' }}>Loading Templates...</div>}>
              <TemplateGallery onSelect={handleTemplateSelect} />
            </Suspense>
          )}
          {(uiState === "document_focused" || uiState === "editing") && document && (
            <div className="document-container fade-in">
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#94a3b8' }}>Loading Document...</div>}>
                <DocumentView document={document} isEditing={uiState === "editing"} lastResponse={lastResponse} />
              </Suspense>
            </div>
          )}
        </div>
        </div>
        )}
      </div>
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
        currentTier={subscription?.tier || 'basic'}
        used={subscription?.usage?.[upgradeFeature] || 0}
        limit={subscription?.limits?.[upgradeFeature] || 0}
      />
      <AiCursor {...cursorState} />
      <SpeedInsights /> {/* for speed insights */}
      <Analytics /> {/* for analytics*/}
    </div>
  );
}

export default App;
